import { useState } from 'react';
import { Modal, List, Button, Input, Space, Typography, Alert, Flex } from 'antd';
import { MailOutlined, LinkOutlined } from '@ant-design/icons';
import { parseInviteToken } from '../../hooks/use_node_invites';

const { Text } = Typography;

/**
 * "Join a node…" behind the node switcher: the invites waiting for this
 * person, each a click away, and a box for an invite link that reached them
 * some other way. `accept(token)` is useMyNodeInvites' (it refreshes the
 * lists); `onJoined(joined)` gets the backend's answer — the switcher opens
 * the node on its own address from there.
 */
export function JoinNodeModal({ open, onClose, invites = [], accept, onJoined }) {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(null); // the token being accepted
    const [error, setError] = useState(null);

    const join = token => {
        setBusy(token);
        setError(null);
        return accept(token)
            .then(joined => { setText(''); onJoined?.(joined); })
            .catch(err => setError(err?.message || 'Could not join that node'))
            .finally(() => setBusy(null));
    };

    const pasted = parseInviteToken(text);

    return (
        <Modal open={open} onCancel={onClose} footer={null} title="Join a node" destroyOnHidden>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {error && <Alert type="error" showIcon title={error} />}
                {invites.length > 0 && (
                    <List
                        size="small"
                        header={<Text type="secondary">Waiting for you</Text>}
                        dataSource={invites}
                        renderItem={invite => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="join"
                                        type="primary"
                                        size="small"
                                        loading={busy === invite.token}
                                        disabled={!!busy && busy !== invite.token}
                                        onClick={() => join(invite.token)}
                                    >
                                        Join
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={<MailOutlined />}
                                    title={invite.node_name}
                                    description={invite.invited_by_name ? `${invite.invited_by_name} invited you` : invite.node_url}
                                />
                            </List.Item>
                        )}
                    />
                )}
                <div>
                    <Text type="secondary">Have an invite link? Paste it here.</Text>
                    <Flex gap={8} style={{ marginTop: 8 }}>
                        <Input
                            prefix={<LinkOutlined />}
                            placeholder="https://acme.vellby.com/join-node/…"
                            value={text}
                            onChange={e => setText(e.target.value)}
                            onPressEnter={() => pasted && join(pasted)}
                            aria-label="Invite link"
                        />
                        <Button type="primary" disabled={!pasted || !!busy} loading={busy === pasted} onClick={() => join(pasted)}>
                            Join
                        </Button>
                    </Flex>
                    {text && !pasted && <Text type="danger" style={{ fontSize: 12 }}>That does not look like an invite link.</Text>}
                </div>
            </Space>
        </Modal>
    );
}

export default JoinNodeModal;
